import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import api from '../hooks/api';

const WARRANTY_WINDOWS = [
  { value: 30, label: 'Next 30 days' },
  { value: 60, label: 'Next 60 days' },
  { value: 90, label: 'Next 90 days' },
  { value: 180, label: 'Next 6 months' },
  { value: 365, label: 'Next 12 months' },
];

const inr = (v) => '₹' + Number(v || 0).toLocaleString('en-IN');
const today = () => new Date().toISOString().slice(0, 10);

function daysLeftLabel(warrantyUntil) {
  if (!warrantyUntil) return '—';
  const diff = Math.round((new Date(warrantyUntil + 'T00:00:00') - new Date(today() + 'T00:00:00')) / 86400000);
  if (diff < 0) return `Expired ${-diff}d ago`;
  if (diff === 0) return 'Expires today';
  return `${diff}d left`;
}

export default function ReportsPage() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [data, setData] = useState(null);
  const [days, setDays] = useState(90);
  const [branchId, setBranchId] = useState('');
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const qs = useCallback((extra = {}) => {
    const p = new URLSearchParams({ days: String(days) });
    if (isAdmin && branchId) p.set('branch_id', branchId);
    Object.entries(extra).forEach(([k, v]) => p.set(k, v));
    return p.toString();
  }, [days, branchId, isAdmin]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const summary = await api.get(`/api/reports/summary?${qs()}`);
      setData(summary);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [qs]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (isAdmin) api.get('/api/branches').then(setBranches).catch(() => {});
  }, [isAdmin]);

  async function exportCsv(path, filename) {
    try { await api.download(`/api/reports/${path}?${qs()}`, filename); }
    catch (e) { alert(e.message); }
  }

  const t = data?.totals;
  const stamp = today();

  return (
    <div className="page">
      <div className="page-header">
        <h1 className="page-title" style={{ margin: 0 }}>Reports</h1>
        <div className="page-actions">
          <button className="btn btn-primary" onClick={() => exportCsv('inventory/export', `inventory-${stamp}.csv`)}>
            Export full inventory
          </button>
        </div>
      </div>

      <div className="filters">
        {isAdmin && (
          <select value={branchId} onChange={e => setBranchId(e.target.value)}>
            <option value="">All Branches</option>
            {branches.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
        )}
        <select value={days} onChange={e => setDays(Number(e.target.value))}>
          {WARRANTY_WINDOWS.map(w => <option key={w.value} value={w.value}>Warranty: {w.label}</option>)}
        </select>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      <div className="metric-grid">
        <div className="metric-card"><div className="metric-label">Total Devices</div><div className="metric-value">{t?.total ?? '—'}</div></div>
        <div className="metric-card"><div className="metric-label">Total Value</div><div className="metric-value">{t ? inr(t.total_cost) : '—'}</div></div>
        <div className="metric-card"><div className="metric-label">Top-level</div><div className="metric-value">{t?.top_level ?? '—'}</div></div>
        <div className="metric-card"><div className="metric-label">Sub-devices</div><div className="metric-value">{t?.sub_devices ?? '—'}</div></div>
      </div>

      {/* Assets by Branch */}
      <div className="section">
        <div className="section-header">
          <h2>Assets by Branch</h2>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCsv('by-branch/export', `assets-by-branch-${stamp}.csv`)}>Export CSV</button>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Branch</th><th>Total</th><th>Assigned</th><th>Available</th><th>In Repair</th><th>Retired</th><th>Lost</th><th>Value</th></tr>
            </thead>
            <tbody>
              {data?.by_branch.map(r => (
                <tr key={r.branch_id}>
                  <td>{r.branch_name}{r.city ? ` · ${r.city}` : ''}</td>
                  <td>{r.total}</td><td>{r.assigned}</td><td>{r.available}</td>
                  <td>{r.in_repair}</td><td>{r.retired}</td><td>{r.lost}</td>
                  <td>{inr(r.total_cost)}</td>
                </tr>
              ))}
              {!loading && data?.by_branch.length === 0 && <tr><td colSpan={8} className="empty">No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assets by Type */}
      <div className="section">
        <div className="section-header">
          <h2>Assets by Type</h2>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCsv('by-type/export', `assets-by-type-${stamp}.csv`)}>Export CSV</button>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Device Type</th><th>Count</th><th>Value</th></tr></thead>
            <tbody>
              {data?.by_type.map(r => (
                <tr key={r.type_id}>
                  <td><span className="tag">{r.type_name}</span></td>
                  <td>{r.total}</td>
                  <td>{inr(r.total_cost)}</td>
                </tr>
              ))}
              {!loading && data?.by_type.length === 0 && <tr><td colSpan={3} className="empty">No data</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* Assets by Status */}
      <div className="section">
        <div className="section-header">
          <h2>Assets by Status</h2>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCsv('by-status/export', `assets-by-status-${stamp}.csv`)}>Export CSV</button>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead><tr><th>Status</th><th>Count</th><th>Value</th></tr></thead>
            <tbody>
              {data?.by_status.map(r => (
                <tr key={r.status}>
                  <td><span className={`badge badge-status badge-${r.status}`}>{r.status.replace('_', ' ')}</span></td>
                  <td>{r.total}</td>
                  <td>{inr(r.total_cost)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Warranty Expiring */}
      <div className="section">
        <div className="section-header">
          <h2>Warranty Expiring{data ? ` (${data.warranty.items.length})` : ''}</h2>
          <button className="btn btn-ghost btn-sm" onClick={() => exportCsv('warranty/export', `warranty-expiring-${days}d-${stamp}.csv`)}>Export CSV</button>
        </div>
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr><th>Asset Tag</th><th>Device</th><th>Type</th><th>Branch</th><th>Assigned To</th><th>Warranty Until</th><th>Status</th></tr>
            </thead>
            <tbody>
              {data?.warranty.items.map(r => {
                const expired = r.warranty_until && r.warranty_until < stamp;
                return (
                  <tr key={r.id}>
                    <td className="mono">{r.asset_tag}</td>
                    <td>{r.name}</td>
                    <td><span className="tag">{r.type_name}</span></td>
                    <td>{r.branch_name}</td>
                    <td>{r.employee_name || '—'}</td>
                    <td style={expired ? { color: 'var(--danger, #c0392b)', fontWeight: 600 } : undefined}>
                      {r.warranty_until} <span style={{ opacity: 0.7, fontWeight: 400 }}>· {daysLeftLabel(r.warranty_until)}</span>
                    </td>
                    <td><span className={`badge badge-status badge-${r.status}`}>{r.status.replace('_', ' ')}</span></td>
                  </tr>
                );
              })}
              {!loading && data?.warranty.items.length === 0 && <tr><td colSpan={7} className="empty">No devices with warranty expiring in this window</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
